<script setup lang="ts">
import { onMounted, onUnmounted, onErrorCaptured, ref, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAppStore, type DemoRole } from '@/stores/app';
import { api } from '@/api';
import {
  loadDemoSession,
  activateDemoMode,
  exitDemoMode,
  clearDemoSession,
} from '@/demo/demo-auth';
import {
  isDemoUiEnabled,
  shouldAutoActivateLocalDemo,
} from '@/demo/demo-mode';
import { getNavSection } from '@/router';

// Shared components used in root layout
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import NavHeader from '@/components/shared/NavHeader.vue';
import ToastContainer from '@/components/shared/ToastContainer.vue';
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue';
import DemoModeBanner from '@/demo/DemoModeBanner.vue';
import RouteProgressBar from '@/components/shared/RouteProgressBar.vue';
import SignInView from '@/components/views/SignInView.vue';

const router = useRouter();
const route = useRoute();
const store = useAppStore();

const renderError = ref<string | null>(null);

let removeClerkListener: (() => void) | null = null;
let removeSkipLinkListener: (() => void) | null = null;

onErrorCaptured((err) => {
  console.error('[App] Uncaught component error:', err);
  renderError.value = err instanceof Error ? err.message : 'An unexpected error occurred';
  return false;
});

const LOCAL_DEMO_ROLE = (
  import.meta.env.VITE_LOCAL_DEMO_ROLE ?? 'landlord'
) as DemoRole;
const DEMO_UI_ENABLED = isDemoUiEnabled(import.meta.env);
const AUTO_LOCAL_DEMO_ENABLED = shouldAutoActivateLocalDemo(
  window.location.hostname,
  import.meta.env,
);

function focusMainContent(): void {
  const main = document.getElementById('main-content');
  if (!main) return;
  if (!main.hasAttribute('tabindex')) {
    main.setAttribute('tabindex', '-1');
  }
  main.focus();
}

function bindSkipLinkFocus(): () => void {
  const handleClick = (event: Event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const skipLink = target.closest('.skip-link');
    if (!(skipLink instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    focusMainContent();
    if (window.location.hash !== '#main-content') {
      window.location.hash = 'main-content';
    }
  };

  document.addEventListener('click', handleClick);
  return () => document.removeEventListener('click', handleClick);
}

onMounted(async () => {
  removeSkipLinkListener = bindSkipLinkFocus();

  // Check for saved demo session BEFORE anything else so route guards work
  const savedRole = loadDemoSession();
  if (savedRole && DEMO_UI_ENABLED) {
    activateDemoMode(savedRole);
    store.authLoading = false;
    return;
  }
  if (savedRole && !DEMO_UI_ENABLED) {
    clearDemoSession();
  }

  // Auto-local demo mode is opt-in to avoid accidental token use on localhost-like setups.
  if (AUTO_LOCAL_DEMO_ENABLED) {
    activateDemoMode(LOCAL_DEMO_ROLE);
    store.authLoading = false;
    return;
  }

  // Initialize Clerk for production
  try {
    if (!window.Clerk) {
      await new Promise<void>((resolve, reject) => {
        const maxWait = 10000;
        const start = Date.now();
        const check = () => {
          if (window.Clerk) {
            resolve();
          } else if (Date.now() - start > maxWait) {
            reject(new Error('Clerk failed to load within timeout'));
          } else {
            setTimeout(check, 100);
          }
        };
        check();
      });
    }

    await window.Clerk!.load();

    if (window.Clerk!.user) {
      store.isAuthenticated = true;
      store.user = window.Clerk!.user;
      const token = await window.Clerk!.session?.getToken();
      if (token) {
        api.setToken(token);
      }
    }

    store.authLoading = false;

    if (!store.isAuthenticated) {
      await nextTick();
      const el = document.getElementById('clerk-sign-in');
      if (el) {
        window.Clerk!.mountSignIn(el, {
          fallbackRedirectUrl: '/',
        });
      }
    }

    removeClerkListener = window.Clerk!.addListener(
      async (resources) => {
        if (store.isDemoMode) return;
        const { user, session } = resources;
        store.isAuthenticated = !!user;
        store.user = user;

        if (session) {
          const token = await session.getToken();
          api.setToken(token);
        }

        if (!user) {
          await nextTick();
          const signInEl = document.getElementById('clerk-sign-in');
          if (signInEl) {
            window.Clerk!.mountSignIn(signInEl, {
              fallbackRedirectUrl: '/',
            });
          }
        }
      },
    );
  } catch (error) {
    console.error('Clerk initialization error:', error);
    store.authLoading = false;
    store.error = 'Failed to initialize authentication';
  }
});

onUnmounted(() => {
  if (removeClerkListener) {
    removeClerkListener();
  }
  if (removeSkipLinkListener) {
    removeSkipLinkListener();
  }
});

async function handleSignOut(): Promise<void> {
  if (store.isDemoMode) {
    exitDemoMode();
    router.push({ name: 'dashboard' });
    return;
  }
  try {
    await window.Clerk!.signOut();
    store.isAuthenticated = false;
    store.user = null;
    api.setToken(null);
    router.push({ name: 'dashboard' });
  } catch (error) {
    console.error('Sign out error:', error);
    store.showToast('Failed to sign out', 'error');
  }
}

function handleDemoLogin(role: DemoRole): void {
  if (!DEMO_UI_ENABLED) {
    store.showToast('Demo mode is disabled for this environment', 'error');
    return;
  }
  activateDemoMode(role);
}
</script>

<template>
  <div>
    <!-- Demo Mode Banner -->
    <DemoModeBanner
      v-if="store.isDemoMode"
      :role="store.demoRole!"
      @exit="exitDemoMode"
    />

    <!-- Main Content -->
    <div :class="{ 'pt-10': store.isDemoMode }">
      <!-- Auth error -->
      <div v-if="store.error" class="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white px-4 py-3 text-center text-sm font-medium shadow-lg">
        {{ store.error }}
      </div>

      <!-- Loading state -->
      <div
        v-if="store.authLoading"
        class="min-h-screen flex items-center justify-center"
        :class="{ 'pt-12': store.error }"
      >
        <LoadingSpinner />
      </div>

      <!-- Not authenticated -->
      <SignInView
        v-else-if="!store.isAuthenticated"
        :demo-enabled="DEMO_UI_ENABLED"
        @demo-login="handleDemoLogin"
      />

      <!-- Authenticated -->
      <div v-else>
        <RouteProgressBar />

        <NavHeader
          :user="store.user"
          :current-view="getNavSection(route.name)"
          :is-demo-mode="store.isDemoMode"
          @sign-out="handleSignOut"
        />

        <main id="main-content" tabindex="-1" class="lg:pb-0 pb-mobile-nav">
          <div v-if="renderError" class="max-w-xl mx-auto mt-16 p-6 bg-red-50 border border-red-200 rounded-lg text-center">
            <h2 class="text-lg font-semibold text-red-800 mb-2">Something went wrong</h2>
            <p class="text-sm text-red-700 mb-4">{{ renderError }}</p>
            <button
              class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
              @click="renderError = null"
            >
              Try Again
            </button>
          </div>
          <RouterView v-else v-slot="{ Component }">
            <Transition name="page" mode="out-in">
              <component :is="Component" :key="$route.path" />
            </Transition>
          </RouterView>
        </main>
      </div>
    </div>

    <!-- Toast notifications -->
    <ToastContainer :toasts="store.toasts" />

    <!-- Confirmation dialog -->
    <ConfirmDialog :dialog="store.confirmDialog" />
  </div>
</template>
